import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectHexes,
  selectPlayers,
  selectSelectedFarmsForConversion,
  selectSpecialBuildings,
  selectUnits
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {selectSelectedFarmHexKey} from '../../../redux/reducers/privateStateReducer/selectors';
import {selectSupplyPlayerPendingSupply} from '../../hexes/calculateTokenImage';
import {calcFarmRemainingSupply} from '../calcFarmRemainingSupply';
import {calculateAvailableSupplyCells} from '../calculateAvailableSupplyCells';
import {getFarmsOfPlayerCities} from '../getPlayerFarms';
import {getGridGeometry} from '../progress/getGridGeometry';
import {calcUnitUpdateBasedOnPendingSupply} from './calcUnitUpdateBasedOnPendingSupply';
import {isUnitFaction} from '../../factions/isUnitFaction';
import {FACTIONS} from '../../../constants/factions';
import {FARM_SUPPLY_BLOCKED_COUNT} from '../../../constants/supply';
import {UNIT_TYPES_ROW_ORDER} from '../../../configs/units';

const compareUnits = (a, b) => (
  UNIT_TYPES_ROW_ORDER.indexOf(a.type) - UNIT_TYPES_ROW_ORDER.indexOf(b.type)
  || String(a.hexKey).localeCompare(String(b.hexKey))
);

/**
 * Сводка фазы снабжения для панели: фермы с нерозданным снабжением, отряды и итог по всем.
 * Рост и голод считаются тем же расчетом, что подпись размера на поле, а достижимость — тем
 * же, что подсветка отрядов при выборе фермы. Порядок отрядов от раздачи не зависит: жетон не
 * должен уезжать из-под пальца
 *
 * @param {Object} state
 * @param {Object|null} player - игрок, за которого клиент раздает снабжение
 * @param {Object} [grid] - ячейки поля
 * @returns {{farms: Object[], units: Object[], totals: Object}}
 */
export const buildSupplyPanelModel = (state, player, grid = getGridGeometry(state)) => {
  const hexes = selectHexes(state);
  const units = selectUnits(state);
  const players = selectPlayers(state);
  const specialBuildings = selectSpecialBuildings(state);
  const buildingsDamage = selectBuildingsDamage(state);
  const citiesCache = selectCitiesCache(state);
  const playerCities = (citiesCache || {})[player.color] || [];
  const pendingSupply = selectSupplyPlayerPendingSupply(state, player);
  const selectedFarmsForConversion = selectSelectedFarmsForConversion(state, player.id);
  const selectedFarmHexKey = selectSelectedFarmHexKey(state);
  const playerUnits = units.filter((unit) => unit.ownerId === player.id).sort(compareUnits);

  const calcRemaining = (farmHexKey, conversion, supply) => calcFarmRemainingSupply(
    farmHexKey, conversion, hexes, specialBuildings, players, units, citiesCache, player, supply, buildingsDamage
  );

  const checkIfUnitTakesSupply = (unit) => isUnitFaction(unit, FACTIONS.GIANTS)
    || !pendingSupply.some((supply) => supply.unitId === unit.id && supply.additionalSupply);

  let produced = 0;
  let distributed = 0;
  const farms = [];
  getFarmsOfPlayerCities(citiesCache, player.color).forEach((hexKey) => {
    const capacity = calcRemaining(hexKey, [], []);
    if (capacity === FARM_SUPPLY_BLOCKED_COUNT) {
      return;
    }
    const remaining = calcRemaining(hexKey, selectedFarmsForConversion, pendingSupply);
    produced += capacity;
    distributed += capacity - remaining;
    if (remaining <= 0) {
      return;
    }
    const reachableCells = new Set(calculateAvailableSupplyCells(
      grid, hexes, specialBuildings, units, hexKey, player, playerCities, buildingsDamage
    ));
    farms.push({
      hexKey,
      remaining,
      capacity,
      reachableCells,
      // Сколько отрядов эта ферма еще может снабдить: 0 — раздавать некому
      takersCount: playerUnits.filter((unit) => reachableCells.has(unit.hexKey) && checkIfUnitTakesSupply(unit)).length,
      isSelected: hexKey === selectedFarmHexKey,
    });
  });

  const unitEntries = playerUnits.map((unit) => {
    const unitSupply = pendingSupply.filter((supply) => supply.unitId === unit.id);
    const {sizeUpdate} = calcUnitUpdateBasedOnPendingSupply(unit, player, pendingSupply);
    const reachableFarmHexKeys = farms
      .filter(({reachableCells}) => reachableCells.has(unit.hexKey))
      .map(({hexKey}) => hexKey);
    return {
      unit,
      basicCount: unitSupply.filter((supply) => !supply.additionalSupply).length,
      hasAdditional: unitSupply.some((supply) => supply.additionalSupply),
      sizeUpdate,
      sizeAfter: unit.size + sizeUpdate,
      isHungry: unitSupply.length === 0,
      isGiant: isUnitFaction(unit, FACTIONS.GIANTS),
      takesSupply: checkIfUnitTakesSupply(unit),
      isUndoable: unitSupply.some((supply) => !supply.isNotUndoable),
      reachableFarmHexKeys,
    };
  });

  return {
    farms,
    units: unitEntries,
    totals: {
      produced,
      distributed,
      remaining: produced - distributed,
      unitsCount: unitEntries.length,
      suppliedCount: unitEntries.filter(({isHungry}) => !isHungry).length,
      additionalCount: unitEntries.filter(({hasAdditional}) => hasAdditional).length,
      hungryCount: unitEntries.filter(({isHungry}) => isHungry).length,
      armyUpdate: unitEntries.reduce((sum, {sizeUpdate}) => sum + sizeUpdate, 0),
    },
  };
};

/** Ключ раздачи снабжения: раунд и игрок */
export const getSupplyTurnKey = (round, playerId) => `${round}:${playerId}`;

// Та, что может снабдить меньше всего отрядов: остальные фермы остаются свободны для тех, до кого
// дотягиваются только они
const pickFarmWithFewestTakers = (farms) => (
  [...farms].sort((a, b) => a.takersCount - b.takersCount || b.remaining - a.remaining)[0] || null
);

/**
 * Какую ферму выделить за игрока: только ту, которой есть кому раздавать
 *
 * @param {Object} model - buildSupplyPanelModel
 * @returns {string|null} ячейка фермы
 */
export const pickAutoSupplyFarm = (model) => (
  pickFarmWithFewestTakers(model.farms.filter(({takersCount}) => takersCount > 0))?.hexKey ?? null
);

/**
 * С какой фермы снабдить отряд: выбранная, если дотягивается, иначе другая подходящая
 *
 * @param {Object} model - buildSupplyPanelModel
 * @param {Object} unitEntry - отряд из model.units
 * @returns {string|null} ячейка фермы
 */
export const pickSupplyFarmForUnit = (model, unitEntry) => {
  const candidates = model.farms.filter(({hexKey}) => unitEntry.reachableFarmHexKeys.includes(hexKey));
  const selected = candidates.find(({isSelected}) => isSelected);
  if (selected) {
    return selected.hexKey;
  }
  return pickFarmWithFewestTakers(candidates)?.hexKey ?? null;
};
